import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, PasswordService } from '@cargos/sprintpay-services';
import { CustomValidators } from '@cargos/sprintpay-utils';
import { Subject, take, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import { ResetPasswordFormErrorsJSON, ResetPasswordFormGroupType } from './models/reset-password-form';
import { formResetPasswordErrors } from './utils/form-errors';

@Component({
    selector: 'app-sprintpay-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    standalone: false,
})
export class ResetPasswdComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void>;
    private activationKey: string;
    public resetFormPassword: ResetPasswordFormGroupType;
    public eyeIconPasswordConfirm = 'visibility_off';
    public eyeIconPassword = 'visibility_off';
    public passwordMatch = false;
    public hidePassword = true;
    public hidePasswordConfirm = true;
    public formErrors: ResetPasswordFormErrorsJSON;

    @Input() widthLogo = '156';
    @Input() heightLogo = '';

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private authService: AuthService,
        private router: Router,
        private passwordService: PasswordService
    ) {
        this.unsubscribe$ = new Subject<void>();
        this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe((params) => {
            this.activationKey = params['activationKey'];
            this.validateActivationKey(this.activationKey);
        });
        this.formErrors = formResetPasswordErrors;
    }

    ngOnInit(): void {
        this.createForm();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    createForm(): void {
        this.resetFormPassword = this.fb.group(
            {
                password: ['', [Validators.required, Validators.minLength(8)]],
                confirmPassword: ['', [Validators.required, Validators.minLength(8)]],
            },
            {
                validator: CustomValidators.match('password', 'confirmPassword'),
            }
        );
    }

    recoverPassword(event, form): void {
        event.preventDefault();
        this.passwordService
            .resetPassword(form, this.activationKey)
            .pipe(take(1))
            .subscribe({
                next: (response) => {
                    Swal.fire({
                        text: 'Your password was updated, now you can sign in.',
                        icon: 'success',
                        customClass: {
                            confirmButton: 'w-100',
                            actions: 'w-100 px-3',
                        },
                        showConfirmButton: true,
                        allowOutsideClick: false,
                    }).then(() => {
                        this.back();
                    });
                },
            });
    }

    back(): void {
        this.router.navigate(['../login']);
    }

    validateActivationKey(_activationKey: string): void {
        this.authService
            .validateActivationKey(_activationKey)
            .pipe(take(1))
            .subscribe({
                error: () => {
                    Swal.fire({
                        text: 'Your activation key was already used, recover your password to get new activation key.',
                        icon: 'error',
                        showConfirmButton: true,
                        confirmButtonText: 'Recover password',
                        customClass: {
                            confirmButton: 'w-100',
                            actions: 'w-100 px-3',
                        },
                        allowOutsideClick: false,
                    }).then(() => {
                        this.router.navigate(['../recover-password']);
                    });
                },
            });
    }
}
